import { IIssueBoardApi } from "@schemas/issueBoardSchema";
import { IOtherAttributesData, IIssueApi } from "@schemas/issueSchema";
import { compile, parse as parseMathExpression } from "mathjs";

const getMathFormulaVariableNames = (expression: string): string[] => {
  const parsedExpression = parseMathExpression(expression);
  const variableNames = new Set<string>();

  parsedExpression.traverse((node, path, parent) => {
    if (!("isSymbolNode" in node) || node.isSymbolNode !== true) {
      return;
    }

    if (parent && "isFunctionNode" in parent && parent.isFunctionNode === true && path === "fn") {
      return;
    }

    variableNames.add((node as unknown as { name: string }).name);
  });

  return Array.from(variableNames);
};

const normalizeMathFormulaNumber = (value: unknown): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== "string") {
    return null;
  }

  const normalizedValue = value.trim().replace(/\s/g, "").replace(",", ".");
  if (normalizedValue.length === 0) {
    return null;
  }

  if (normalizedValue.endsWith("%")) {
    const percentageValue = normalizedValue.slice(0, -1);
    if (percentageValue.length === 0) {
      return null;
    }

    const parsedPercentage = Number(percentageValue);
    return Number.isFinite(parsedPercentage) ? parsedPercentage / 100 : null;
  }

  const parsedNumber = Number(normalizedValue);
  return Number.isFinite(parsedNumber) ? parsedNumber : null;
};

export const getIssuesWithMathFormulaAdded = ({
  issues,
  issueBoard,
}: {
  issues: IIssueApi[];
  issueBoard: IIssueBoardApi;
}): IIssueApi[] => {
  if (!issues?.length || !issueBoard?.otherAttributesConfig) {
    return issues;
  }

  const otherAttributesConfig = issueBoard.otherAttributesConfig;
  const supportedAttributeKeys = new Set(
    Object.entries(otherAttributesConfig)
      .filter(
        ([, attribute]) =>
          attribute.isActive && (attribute.typeOfValue === "NUMBER" || attribute.typeOfValue === "DROPDOWN"),
      )
      .map(([key]) => key),
  );

  const mathFormulaAttributes = Object.entries(otherAttributesConfig).filter(
    ([, attribute]) => attribute.typeOfValue === "MATH_FORMULA" && attribute.isActive,
  );

  if (mathFormulaAttributes.length === 0) {
    return issues;
  }

  return issues.map((issue) => {
    const nextOtherAttributesData: NonNullable<IOtherAttributesData> = {
      ...(issue.otherAttributesData ?? {}),
    };

    for (const [attributeKey, attribute] of mathFormulaAttributes) {
      const expression = attribute.mathFormula?.expression?.trim();

      if (!expression) {
        nextOtherAttributesData[attributeKey] = "";
        continue;
      }

      try {
        const usedVariables = getMathFormulaVariableNames(expression);
        const hasInvalidVariable = usedVariables.some((variableName) => !supportedAttributeKeys.has(variableName));

        if (hasInvalidVariable) {
          nextOtherAttributesData[attributeKey] = "";
          continue;
        }

        const scope: Record<string, number> = {};
        let isScopeValid = true;

        for (const variableName of usedVariables) {
          const variableValue = normalizeMathFormulaNumber(nextOtherAttributesData[variableName]);
          if (variableValue == null) {
            isScopeValid = false;
            break;
          }
          scope[variableName] = variableValue;
        }

        if (!isScopeValid) {
          nextOtherAttributesData[attributeKey] = "";
          continue;
        }

        const evaluatedResult = compile(expression).evaluate(scope);
        const normalizedResult = normalizeMathFormulaNumber(evaluatedResult);

        nextOtherAttributesData[attributeKey] = normalizedResult ?? "";
      } catch {
        nextOtherAttributesData[attributeKey] = "";
      }
    }

    return {
      ...issue,
      otherAttributesData: nextOtherAttributesData,
    };
  });
};
